/**
 * Pre-shrink HTMLImageElement до `maxSize × maxSize` через canvas, возвращает
 * `ImageData` для дальнейшей передачи в `extractLab` (см. ADR-0009).
 *
 * Зачем: фото с современных смартфонов (iPhone 12+, флагманские Android) дают
 * 12-50 МП. ImageData = 4 байта/пиксель → 12 МП = 48 МБ, 50 МП = 200 МБ.
 * На слабых устройствах canvas.getImageData падает или OOM-ит. Pre-shrink до
 * 1024×1024 даёт ~4 МБ и стабильную работу.
 *
 * Точность LAB-подбора при downscale: bilinear-усреднение пикселей сохраняет
 * среднюю яркость и цвет региона. Для подбора декоров (по среднему цвету
 * круга или всего фото) разрешения 1024×1024 более чем достаточно.
 *
 * No-op pass-through для image ≤ maxSize: scale=1, dims preserved.
 *
 * TODO(B-024): EXIF orientation handling. Современные браузеры (Chrome 81+,
 * Safari 13.4+, Firefox 77+) применяют EXIF orientation к canvas автоматически
 * при `drawImage(<img>)`. Если пользователи на старых браузерах сообщат о
 * проблемах с поворотом — добавить explicit EXIF normalization step (читать
 * через File→ArrayBuffer→EXIF reader, ротировать canvas вручную перед drawImage).
 *
 * P-009 не нарушается: ImageData остаётся в памяти клиента, не сериализуется
 * для отправки. Эта функция — внутреннее звено pipeline File → ImageData →
 * extractLab → {l,a,b} (см. ADR-0009 §1).
 */

export interface DownscaleResult {
  imageData: ImageData;
  /** Применённый scale factor: 1.0 = без сжатия. */
  scale: number;
  /** Финальная ширина и высота canvas (= ImageData.width/height). */
  width: number;
  height: number;
}

export const MAX_DOWNSCALE_SIZE = 1024;

export function downscaleImageToImageData(
  image: HTMLImageElement,
  maxSize: number = MAX_DOWNSCALE_SIZE,
): DownscaleResult {
  const naturalW = image.naturalWidth;
  const naturalH = image.naturalHeight;
  if (naturalW === 0 || naturalH === 0) {
    throw new Error('downscaleImageToImageData: image has zero dimensions');
  }

  const longest = Math.max(naturalW, naturalH);
  const scale = longest > maxSize ? maxSize / longest : 1;
  const targetW = Math.max(1, Math.round(naturalW * scale));
  const targetH = Math.max(1, Math.round(naturalH * scale));

  const canvas = document.createElement('canvas');
  canvas.width = targetW;
  canvas.height = targetH;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('downscaleImageToImageData: 2D canvas context unavailable');
  }
  ctx.drawImage(image, 0, 0, targetW, targetH);
  const imageData = ctx.getImageData(0, 0, targetW, targetH);

  return { imageData, scale, width: targetW, height: targetH };
}
